import { ACCOUNT_TYPE, ITEM_ACTIVITY, ITEM_STATUS, LANGUAGES_SUPPORTED, LanguagesSupported, STATUS_TYPE } from '@/types/enums';
import { LanguageInfo } from '@/types/language';
import { Route } from '@/types/routes';
import { PaginationState } from '@tanstack/react-table';
import { enUS, vi, ja } from 'date-fns/locale';

// #region Routes
enum RoutesKey {
    home,
    dashboard,
    login,
    button,
    calendar,
    pagination,
    table,
    reactToastify,
    modal,
    tabs,
    userManagement,
    itemManagement,
    setting,
    libraryManagement,
    addItem,
    updateItem,
    landmarkManagement,
    businessDashboard,
    operationsMap,
    omikujiTextsManagement,
    eventTrackingDashboard,
    sponsorManagement,
    sponsoredCampaignManagement,
    // T3-B PR 4-bis (2026-05-12)
    tierPresetManagement,
    motifTemplateSetManagement,
    templateMotifManagement,
    // Coupon management redesign (Phase 2 PR 4, 2026-05-15)
    couponTemplateManagement,
    // 2026-07-07: 操作・項目説明ヘルプ
    help
};

export const ROUTES: Record<keyof typeof RoutesKey, Route> = {
    userManagement: {
        href: '/users',
        title: 'words_title.user_management',
        menuLabel: 'words_title.user_management',
        breadcrumbLabel: 'words_title.user_management'
    },
    itemManagement: {
        href: '/items',
        title: 'words_title.item_management',
        menuLabel: 'words_title.item_management',
        breadcrumbLabel: 'words_title.item_management'
    },
    addItem: {
        href: 'add',
        title: 'words_title.add_item',
        menuLabel: 'words_title.add_item',
        breadcrumbLabel: 'words_title.add_item',
        pathKey: 'items/add'
    },
    updateItem: {
        href: 'update',
        title: 'words_title.update_item',
        menuLabel: 'words_title.update_item',
        breadcrumbLabel: 'words_title.update_item',
        pathKey: 'items/update'
    },
    libraryManagement: {
        href: '/photo-library',
        title: 'words_title.library_management',
        menuLabel: 'words_title.library_management',
        breadcrumbLabel: 'words_title.library_management'
    },
    setting: {
        href: '/setting',
        title: 'words_title.setting',
        menuLabel: 'words_title.setting',
        breadcrumbLabel: 'words_title.setting'
    },
    landmarkManagement: {
        href: '/landmarks',
        title: 'words_title.landmark_management',
        menuLabel: 'words_title.landmark_management',
        breadcrumbLabel: 'words_title.landmark_management'
    },
    businessDashboard: {
        href: '/business-dashboard',
        title: 'words_title.business_dashboard',
        menuLabel: 'words_title.business_dashboard',
        breadcrumbLabel: 'words_title.business_dashboard'
    },
    operationsMap: {
        href: '/operations-map',
        title: 'words_title.operations_map',
        menuLabel: 'words_title.operations_map',
        breadcrumbLabel: 'words_title.operations_map'
    },
    omikujiTextsManagement: {
        href: '/omikuji-texts',
        title: 'words_title.omikuji_texts_management',
        menuLabel: 'words_title.omikuji_texts_management',
        breadcrumbLabel: 'words_title.omikuji_texts_management'
    },
    eventTrackingDashboard: {
        href: '/event-tracking-dashboard',
        title: 'words_title.event_tracking_dashboard',
        menuLabel: 'words_title.event_tracking_dashboard',
        breadcrumbLabel: 'words_title.event_tracking_dashboard'
    },
    // T3-B sponsored motif (PR 4, 2026-05-07)
    sponsorManagement: {
        href: '/sponsors',
        title: 'words_title.sponsor_management',
        menuLabel: 'words_title.sponsor_management',
        breadcrumbLabel: 'words_title.sponsor_management'
    },
    sponsoredCampaignManagement: {
        href: '/sponsored-campaigns',
        title: 'words_title.sponsored_campaign_management',
        menuLabel: 'words_title.sponsored_campaign_management',
        breadcrumbLabel: 'words_title.sponsored_campaign_management'
    },
    // T3-B PR 4-bis (2026-05-12): omikuji ガチャフレームワーク管理 (= roadmap v3 §8)
    tierPresetManagement: {
        href: '/tier-presets',
        title: 'words_title.tier_preset_management',
        menuLabel: 'words_title.tier_preset_management',
        breadcrumbLabel: 'words_title.tier_preset_management'
    },
    motifTemplateSetManagement: {
        href: '/motif-template-sets',
        title: 'words_title.motif_template_set_management',
        menuLabel: 'words_title.motif_template_set_management',
        breadcrumbLabel: 'words_title.motif_template_set_management'
    },
    templateMotifManagement: {
        href: '/template-motifs',
        title: 'words_title.template_motif_management',
        menuLabel: 'words_title.template_motif_management',
        breadcrumbLabel: 'words_title.template_motif_management'
    },
    // Coupon management redesign (Phase 2 PR 4, 2026-05-15)
    couponTemplateManagement: {
        href: '/coupon-templates',
        title: 'words_title.coupon_template_management',
        menuLabel: 'words_title.coupon_template_management',
        breadcrumbLabel: 'words_title.coupon_template_management'
    },
    // 2026-07-07: 操作・項目説明ヘルプページ (静的、Phase B)。
    // menuLabel/title は i18n キーではなく直接日本語 (t() は未登録キーを素通しするため、そのまま表示される)。
    help: {
        href: '/help',
        title: '操作ガイド',
        menuLabel: 'ヘルプ / 操作ガイド',
        breadcrumbLabel: '操作ガイド'
    },
    home: {
        href: '/',
        title: 'words_title.home',
        menuLabel: 'words_title.home',
        breadcrumbLabel: 'words_title.home'
    },
    dashboard: {
        href: '/dashboard',
        title: 'words_title.dashboard',
        menuLabel: 'words_title.dashboard',
        breadcrumbLabel: 'words_title.dashboard'
    },
    login: {
        href: '/login',
        menuLabel: ''
    },
    button: {
        href: '/button',
        title: 'Button',
        menuLabel: 'Button',
        breadcrumbLabel: 'Button'
    },
    calendar: {
        href: '/calendar',
        title: 'Calendar',
        menuLabel: 'Calendar',
        breadcrumbLabel: 'Calendar'
    },
    pagination: {
        href: '/pagination',
        title: 'Pagination',
        menuLabel: 'Pagination',
        breadcrumbLabel: 'Pagination'
    },
    table: {
        href: '/table',
        title: 'Table',
        menuLabel: 'Table',
        breadcrumbLabel: 'Table'
    },
    reactToastify: {
        href: '/react-toastify',
        title: 'React Toastify',
        menuLabel: 'React Toastify',
        breadcrumbLabel: 'React Toastify'
    },
    modal: {
        href: '/modal',
        title: 'Modal',
        menuLabel: 'Modal',
        breadcrumbLabel: 'Modal'
    },
    tabs: {
        href: '/tabs',
        title: 'Tabs',
        menuLabel: 'Tabs',
        breadcrumbLabel: 'Tabs'
    }
};

// #endregion Routes

// #region Languages
export const DEFAULT_LANGUAGE: LanguagesSupported = LANGUAGES_SUPPORTED.Japanese;

export const LANGUAGES_MAPPER: {
    japanese: LanguageInfo,
    english: LanguageInfo,
    vietnamese: LanguageInfo
} = {
    japanese: {
        code: LANGUAGES_SUPPORTED.Japanese,
        fullname: 'words_title.japanese',
        shortName: 'JA',
        flag: 'jp',
        dateFns: ja
    },
    english: {
        code: LANGUAGES_SUPPORTED.English,
        fullname: 'words_title.english',
        shortName: 'EN',
        flag: 'gb',
        dateFns: enUS
    },
    vietnamese: {
        code: LANGUAGES_SUPPORTED.Vietnamese,
        fullname: 'words_title.vietnamese',
        shortName: 'VI',
        flag: 'vn',
        dateFns: vi
    }
};

// #endredion

// #region Dates

export const DATE_YMD = 'yyyy/MM/dd';

// #endregion

// #region Table

export const DEFAULT_SELECT_PAGE_SIZES: number[] = [10, 20, 50, 100];

export const DEFAULT_PAGINATION: PaginationState = {
    pageIndex: 0,
    pageSize: 10
};

// #endregion

// #region Types
export const STATUS_TYPES = [
    {
        id: STATUS_TYPE.ACTIVE,
        name: 'field.active'
    },
    {
        id: STATUS_TYPE.INACTIVE,
        name: 'field.inactive'
    }
];

export const ACCOUNT_TYPES = [
    {
        id: ACCOUNT_TYPE.GOOGLE,
        name: 'field.google'
    },
    {
        id: ACCOUNT_TYPE.LINE,
        name: 'field.line'
    },
    {
        id: ACCOUNT_TYPE.TWITTER,
        name: 'field.twitter'
    },
    {
        id: ACCOUNT_TYPE.PASSKEY,
        name: 'field.passkey'
    }
];

export const ITEMS_STATUS = [
    {
        id: ITEM_STATUS.UN_PUBLISHED,
        name: 'field.unpublished',
        style: 'bg-[#5A5A5A]',
        textColor: 'text-[#5A5A5A]'
    },
    {
        id: ITEM_STATUS.PUBLISHED,
        name: 'field.published',
        style: 'bg-[#20DA6D]',
        textColor: 'text-[#20DA6D]'
    },
    {
        id: ITEM_STATUS.PRIVATE,
        name: 'field.private',
        style: 'bg-[#A90E0E]',
        textColor: 'text-[#A90E0E]'
    }
];

export const ITEM_ACTIVITYS = [
    {
        id: ITEM_ACTIVITY.NONE,
        name: 'field.none'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__ON_MAP,
        name: 'field.on_map'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__GET_IT,
        name: 'field.get_it'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__USED,
        name: 'field.used'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__REPORT,
        name: 'field.report'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__RESTORE,
        name: 'field.restore'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__WITHDRAWAL,
        name: 'field.withdrawal_2'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__EXPIRED,
        name: 'field.expire'
    }
];

export const ACTIVITY_COLOR = [
    {
        id: ITEM_ACTIVITY.PUBLISHED__ON_MAP,
        name: 'field.on_map',
        style: 'bg-gradient-to-r from-[#35EA8B] to-[#43F4BF]'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__USED,
        name: 'field.used',
        style: 'bg-gradient-to-r from-[#35EA8B] to-[#43F4BF]'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__GET_IT,
        name: 'field.get_it',
        style: 'bg-gradient-to-r from-[#356FEA] to-[#F443AE]'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__REPORT,
        name: 'field.report',
        style: 'bg-gradient-to-r from-[#FF200E] to-[#F44343]'
    },
    {
        id: ITEM_ACTIVITY.PUBLISHED__RESTORE,
        name: 'field.restore',
        style: 'bg-gradient-to-r from-[#FFAD00] to-[#E5D541]'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__WITHDRAWAL,
        name: 'field.withdrawal_2',
        style: 'bg-gradient-to-r from-[#EA355D] to-[#F44343]'
    },
    {
        id: ITEM_ACTIVITY.PRIVATE__EXPIRED,
        name: 'field.expire',
        style: 'bg-gradient-to-r from-[#979797] to-[#BFBFBF]'
    }
];
// #endregion

export const SETTING_TIME_AUTO_DELETE_ITEM = 'settings_time_auto_delete_item';
// drop-ux-revamp: 管理画面で編集可能な配置関連設定
export const SETTING_DAILY_DROP_LIMIT = 'settings_daily_drop_limit';
export const SETTING_DROPPABLE_RADIUS_M = 'settings_droppable_radius_m';
// 2026-04-26: エンドユーザの探索範囲も CMS で調整可能に。
// backend ItemService.getNearbyItems が SettingService 経由で読み取る。
export const SETTING_SEARCHABLE_RADIUS_M = 'settings_searchable_radius_m';
// shake-acquisition (2026-04-25): 振り取得の判定パラメータ。
// 都度 PR/release を回さず CMS で tuning 可能にしたもの。
// frontend 側 (fe-enduser) で URL knob > サーバ値 > hardcoded default の優先順で適用される。
export const SETTING_SHAKE_THRESHOLD = 'settings_shake_threshold';
export const SETTING_SHAKE_WINDOW_MS = 'settings_shake_window_ms';
export const SETTING_SHAKE_DEADBAND = 'settings_shake_deadband';
// Shake to Spark v2 (2026-05-27): 取得モーダル v2 paradigm 用 settings。
// fe-enduser の AccuracyScorer + ShakeSparkOverlay が dropConfig 経由で読み取り、 取得判定に使用。
// Spec: design/shake-spark-rhythm-game-v2-spec.md §3.2 / §11.1
//   - completion_threshold: 取得 gate の Groove % 閾値 (= default 65、 「軽く雑では取得不能、 ある程度頑張れば取得」 のバランス点)
//   - duration_sec: active phase の最大秒 (= default 15)
//   - beats_per_action: 何拍ごとに 1 振りか (= default 4、 BGM 1 小節ごとに 1 振り)
//   - predict_duration_sec: 予告 phase の最大秒 (= default 5、 server 側で 1-30 clamp)
export const SETTING_SHAKE_COMPLETION_THRESHOLD = 'settings_shake_completion_threshold';
export const SETTING_SHAKE_DURATION_SEC = 'settings_shake_duration_sec';
export const SETTING_SHAKE_BEATS_PER_ACTION = 'settings_shake_beats_per_action';
export const SETTING_SHAKE_PREDICT_DURATION_SEC = 'settings_shake_predict_duration_sec';
// 2026-05-27: voice command ON/OFF toggle (= v2 ↔ v1 切替)。
// OFF にすると enduser は voice 表示・発生を完全 skip し、 旧 v1 (= shakeAccumulator + 揺れゲージ) で取得判定。
// BGM は OFF でも流す (= リズム game 寄せだけ無効化)。
// default は true (= v2 paradigm 維持、 backend seed と一致)。
export const SETTING_VOICE_COMMAND_ENABLED = 'settings_voice_command_enabled';
// 2026-09-30: カプセル打ち上げ演出 (第2段) の全体 ON/OFF。
// OFF (default) は今の v1 と完全に同じ動き。本番は OFF で出し、基準値がそろってから ON にする。
export const SETTING_SHAKE_EFFECTS_ENABLED = 'settings_shake_effects_enabled';
// 1 ユーザーあたり累計で作成可能なアイテム数 (default 5)。
// 課金プラン拡張時は users.plan_type を参照する形に切替予定 (将来 task)。
export const SETTING_MAX_ITEMS_PER_USER = 'settings_max_items_per_user';

// MEO landmark ads (Phase 1, 2026-05-01): 事業者軸の運用パラメータ。
// api 側の migrations/01_migration_up.sql で seed 済み。Step 4-5 (Phase B)
// で integer 系の 3 キーから edit UI を提供。残る boolean / float / JSON /
// string 系のキー (MEO_ADS_ENABLED / COMMERCIAL_RATIO_CAP / OMIKUJI_KYO_BONUS_MULTIPLIER /
// OMIKUJI_FORTUNE_DISTRIBUTION / OMIKUJI_LLM_MODEL) は別 PR で順次追加。
export const SETTING_COMMERCIAL_DAILY_LIMIT = 'COMMERCIAL_DAILY_LIMIT';
export const SETTING_COMMERCIAL_PLACEMENT_RADIUS_M = 'COMMERCIAL_PLACEMENT_RADIUS_M';
export const SETTING_OMIKUJI_KYO_BONUS_DURATION_HOURS = 'OMIKUJI_KYO_BONUS_DURATION_HOURS';
// Step 4-5 続き (2026-05-01): boolean フラグ + LLM モデル select。
export const SETTING_MEO_ADS_ENABLED = 'MEO_ADS_ENABLED';
export const SETTING_OMIKUJI_LLM_MODEL = 'OMIKUJI_LLM_MODEL';

// Step 4-5 続き (2026-05-01): float keys。CMS 上で小数 (0.4 / 1.10 等) を編集可能に。
export const SETTING_COMMERCIAL_RATIO_CAP = 'COMMERCIAL_RATIO_CAP';
export const SETTING_OMIKUJI_KYO_BONUS_MULTIPLIER = 'OMIKUJI_KYO_BONUS_MULTIPLIER';

// Step 4-5 完結 (2026-05-01): JSON key。占い結果 5 階層別の出現確率 (合計 100)。
// api 側 SettingService.getValueSettingCommon が JSON.parse して使用する想定。
export const SETTING_OMIKUJI_FORTUNE_DISTRIBUTION = 'OMIKUJI_FORTUNE_DISTRIBUTION';

// 宿 QR (2026-09-25, 民泊 PoC Step 1): 宿 QR 経由の配布上限。api 側 migration §43 で seed 済。
export const SETTING_FACILITY_QR_DAILY_CAP = 'FACILITY_QR_DAILY_CAP';
export const SETTING_FACILITY_QR_PER_USER_TEMPLATE_LIMIT = 'FACILITY_QR_PER_USER_TEMPLATE_LIMIT';

// 占い結果 5 階層の i18n キー / DB key 一覧。order は spec §抽選アルゴリズム に合わせる。
export const OMIKUJI_FORTUNE_TIERS = [
    { key: 'daikichi', label: '大吉' },
    { key: 'chukichi', label: '中吉' },
    { key: 'shokichi', label: '小吉' },
    { key: 'suekichi', label: '末吉' },
    { key: 'kyo', label: '凶' }
] as const;

// 占い文言生成に使う LLM モデル候補。本リストにない値が api 側に保存されていた場合は
// fetchData が default にフォールバックする (yup schema の oneOf チェックを通すため)。
export const OMIKUJI_LLM_MODEL_OPTIONS = [
    { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (default、低コスト)' },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (高品質、コスト高)' }
];
